import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import DesktopLayout from '../../components/layout/DesktopLayout';
import ProductCard from '../../components/products/ProductCard';
import ExpandableFilterPanel from '../../components/products/ExpandableFilterPanel';
import PriceRangeFilter from '../../components/products/PriceRangeFilter';
import StockStatusFilter from '../../components/products/StockStatusFilter';
import MultiCategoryFilter from '../../components/products/MultiCategoryFilter';
import SortOptions from '../../components/products/SortOptions';
import { getProducts, getProductCategories } from '../../services/productsApi';
import type { ProductListItem, ProductCategory } from '../../types';
import './ProductsListPage.css';

/**
 * 商品列表頁面
 */
const ProductsListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // Parse filter state from URL
  const categoriesParam = searchParams.get('categories');
  const selectedCategories = useMemo(() => 
    categoriesParam ? categoriesParam.split(',') : [],
    [categoriesParam]
  );
  const minPrice = searchParams.get('minPrice') ? Number(searchParams.get('minPrice')) : undefined;
  const maxPrice = searchParams.get('maxPrice') ? Number(searchParams.get('maxPrice')) : undefined;
  const inStockOnly = searchParams.get('inStock') === 'true';
  const sortBy = (searchParams.get('sortBy') as 'date' | 'price' | 'popularity' | 'name') || 'date';
  const sortOrder = (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc';
  const searchParam = searchParams.get('search');
  const pageParam = searchParams.get('page');
  const currentPage = useMemo(() => pageParam ? parseInt(pageParam) : 1, [pageParam]);
  const limit = 20;
  const offset = (currentPage - 1) * limit;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const [productsResponse, categoriesResponse] = await Promise.all([
        getProducts({
          categories: selectedCategories.length > 0 ? selectedCategories : undefined,
          search: searchParam || undefined,
          minPrice,
          maxPrice,
          inStock: inStockOnly || undefined,
          sortBy,
          sortOrder,
          limit,
          offset,
        }),
        getProductCategories(),
      ]);

      if (productsResponse.success) {
        setProducts(productsResponse.data);
        setTotal(productsResponse.pagination.total);
      }

      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data);
      }

      setLoading(false);
    };

    fetchData();
  }, [selectedCategories, searchParam, minPrice, maxPrice, inStockOnly, sortBy, sortOrder, currentPage]);

  const updateURL = (updates: Record<string, string | null>) => {
    const newParams: Record<string, string> = {};
    if (searchParam) newParams.search = searchParam;
    if (selectedCategories.length > 0) newParams.categories = selectedCategories.join(',');
    if (minPrice) newParams.minPrice = String(minPrice);
    if (maxPrice) newParams.maxPrice = String(maxPrice);
    if (inStockOnly) newParams.inStock = 'true';
    if (sortBy !== 'date') newParams.sortBy = sortBy;
    if (sortOrder !== 'desc') newParams.sortOrder = sortOrder;
    newParams.page = '1';

    Object.entries(updates).forEach(([key, value]) => {
      if (value === null) {
        delete newParams[key];
      } else {
        newParams[key] = value;
      }
    });

    setSearchParams(newParams);
  };

  const handleCategoriesChange = (categories: string[]) => {
    updateURL({ categories: categories.length > 0 ? categories.join(',') : null });
  };

  const handlePriceChange = (min?: number, max?: number) => {
    updateURL({
      minPrice: min?.toString() || null,
      maxPrice: max?.toString() || null,
    });
  };

  const handleStockFilterChange = (inStock: boolean) => {
    updateURL({ inStock: inStock ? 'true' : null });
  };

  const handleSortChange = (newSortBy: typeof sortBy, newSortOrder: typeof sortOrder) => {
    updateURL({
      sortBy: newSortBy !== 'date' ? newSortBy : null,
      sortOrder: newSortOrder !== 'desc' ? newSortOrder : null,
    });
  };

  const handleClearAll = () => {
    updateURL({
      categories: null,
      minPrice: null,
      maxPrice: null,
      inStock: null,
    });
  };

  const handleSearchChange = (search: string) => {
    updateURL({ search: search || null });
  };

  const handleProductClick = (productId: string) => {
    window.location.href = `/products/${productId}`;
  };

  const totalPages = Math.ceil(total / limit);

  if (loading) {
    return (
      <DesktopLayout>
        <div className="products-list-page">
          <div className="products-list-page__loading">載入中...</div>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout>
      <div className="products-list-page">
        <header className="products-list-page__header">
          <h1>商品列表</h1>

          {/* Search */}
          <div className="products-list-page__search">
            <input
              type="text"
              placeholder="搜尋商品..."
              value={searchParam || ''}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>

          {/* Advanced Filters */}
          <ExpandableFilterPanel title="進階篩選" defaultExpanded={false}>
            <MultiCategoryFilter
              categories={categories}
              selectedCategories={selectedCategories}
              onCategoriesChange={handleCategoriesChange}
            />
            <PriceRangeFilter
              minPrice={minPrice}
              maxPrice={maxPrice}
              onPriceChange={handlePriceChange}
            />
            <StockStatusFilter
              inStockOnly={inStockOnly}
              onStockFilterChange={handleStockFilterChange}
            />
            <SortOptions
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
            />
            <button
              className="products-list-page__clear-filters"
              onClick={handleClearAll}
              type="button"
            >
              清除所有篩選
            </button>
          </ExpandableFilterPanel>
        </header>

        {/* Product count */}
        <div className="products-list-page__count">
          共 {total} 件商品
        </div>

        {/* Product grid */}
        {products.length === 0 ? (
          <div className="products-list-page__empty">
            <p>找不到符合條件的商品</p>
          </div>
        ) : (
          <>
            <div className="products-list-page__grid">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onClick={handleProductClick}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="products-list-page__pagination">
                <button
                  disabled={currentPage === 1}
                  onClick={() => {
                    const newParams: Record<string, string> = {};
                    if (searchParam) newParams.search = searchParam;
                    if (selectedCategories.length > 0) newParams.categories = selectedCategories.join(',');
                    if (minPrice) newParams.minPrice = String(minPrice);
                    if (maxPrice) newParams.maxPrice = String(maxPrice);
                    if (inStockOnly) newParams.inStock = 'true';
                    if (sortBy !== 'date') newParams.sortBy = sortBy;
                    if (sortOrder !== 'desc') newParams.sortOrder = sortOrder;
                    newParams.page = String(currentPage - 1);
                    setSearchParams(newParams);
                  }}
                >
                  上一頁
                </button>

                <span className="products-list-page__page-info">
                  第 {currentPage} / {totalPages} 頁
                </span>

                <button
                  disabled={currentPage === totalPages}
                  onClick={() => {
                    const newParams: Record<string, string> = {};
                    if (searchParam) newParams.search = searchParam;
                    if (selectedCategories.length > 0) newParams.categories = selectedCategories.join(',');
                    if (minPrice) newParams.minPrice = String(minPrice);
                    if (maxPrice) newParams.maxPrice = String(maxPrice);
                    if (inStockOnly) newParams.inStock = 'true';
                    if (sortBy !== 'date') newParams.sortBy = sortBy;
                    if (sortOrder !== 'desc') newParams.sortOrder = sortOrder;
                    newParams.page = String(currentPage + 1);
                    setSearchParams(newParams);
                  }}
                >
                  下一頁
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </DesktopLayout>
  );
};

export default ProductsListPage;
